// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * AssignVetModal — assign a patient to one or several vets (vetpo wiring).
 *
 * POSTs to POST /api/pets/:id/assign-vet (ada-core src/collections/Pets/assignVet.ts)
 * with `{ assignedVetIds }`. The master ticks any of {itself, each ACTIVE
 * sub-account}; ticking nobody is Unassigned → `[]` → the master keeps
 * receiving the alerts (vic/vetpo#70). Only THIS clinic's team ids are sent:
 * the server keeps another clinic's assignees on a shared patient and answers
 * 422 if one of theirs is sent. Non-active sub-accounts are not offered (see
 * the filter note below).
 */
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useGetIdentity, useGetList, useNotify, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'
import { effectiveSubAccountStatus } from './subAccountStatus'

interface AssignVetModalProps {
  open: boolean
  petId: number
  petName?: string
  /** The pet's current assignees (`pet.assignedVetIds`); ids outside this
   *  clinic's team are ignored, not sent back. */
  currentAssignedVetIds: number[]
  onClose: (changed: boolean) => void
}

const AssignVetModal: React.FC<AssignVetModalProps> = ({
  open,
  petId,
  petName,
  currentAssignedVetIds,
  onClose,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { identity } = useGetIdentity()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const { data: subs = [], isLoading } = useGetList(
    'users',
    {
      filter: { masterAccount: identity?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: open && !!identity?.id },
  )

  // Pre-fill = current assignees ∩ this clinic's team (the master + its subs).
  // Seeded once the team has loaded; until then nothing is ticked and Save is
  // held, or a fast click would send [] and unassign.
  const teamLoaded = identity?.id != null && !isLoading
  const teamIds = useMemo(
    () => new Set<number>([Number(identity?.id), ...subs.map((u: any) => Number(u.id))]),
    [identity, subs],
  )
  const [picked, setPicked] = useState<Set<number> | null>(null)
  const ticked = useMemo(
    () => picked ?? new Set(currentAssignedVetIds.filter((id) => teamIds.has(id))),
    [picked, currentAssignedVetIds, teamIds],
  )
  const toggle = (id: number) => {
    const next = new Set(ticked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
  }

  // A vet with email alerts OFF swallows the patient's alerts entirely.
  // ada-core's getAlertRecipientsV2 filters every recipient by
  // email_alerts_enabled and has NO broadcast fallback, so assigning a patient
  // to a muted vet means nobody is emailed — not the master, not the clinic.
  // `null`/absent means enabled (the field defaults true and the backend
  // COALESCEs it), so only an explicit `false` is muted.
  const isMuted = useCallback(
    (candidateId: number): boolean => {
      if (identity?.id != null && candidateId === Number(identity.id)) {
        return (identity as any)?.emailAlertsEnabled === false
      }
      const sub = subs.find((u: any) => Number(u.id) === candidateId)
      return sub ? sub.emailAlertsEnabled === false : false
    },
    [identity, subs],
  )
  // The warning is per ticked vet (VP-TEAM R26), named so a master can see which.
  const mutedTicked = Array.from(ticked).filter(isMuted)
  const nameOf = (id: number): string => {
    if (identity?.id != null && id === Number(identity.id)) {
      return translate('pos.clinic_team.assign_to_me')
    }
    const sub = subs.find((u: any) => Number(u.id) === id)
    return sub?.name || sub?.email || `#${id}`
  }

  const handleSubmit = async () => {
    setError('')
    setSubmitting(true)
    try {
      const assignedVetIds = Array.from(ticked).sort((x, y) => x - y)
      const response = await deadlineFetch(`/api/pets/${petId}/assign-vet`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ assignedVetIds }),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 200 && data?.success) {
        notify('pos.clinic_team.assign_success', { type: 'success' })
        onClose(true)
        return
      }
      if (response.status === 409) {
        // SHARED_PET_NOT_ASSIGNABLE: the pet is visible only through a
        // vet-share, so this clinic cannot assign it. The server's sentence
        // says so; show it.
        setError(data?.errors?.[0]?.message || translate('pos.clinic_team.error_generic'))
      } else if (response.status === 422) {
        setError(translate('pos.clinic_team.assign_not_in_team'))
      } else {
        setError(data?.errors?.[0]?.message || translate('pos.clinic_team.error_generic'))
      }
    } catch (_e) {
      notify('pos.clinic_team.error_network', { type: 'error' })
      setError(translate('pos.clinic_team.error_network'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      // This dialog is mounted INSIDE a Datagrid row (AssignVetButton lives in
      // a row cell). MUI portals it in the DOM, but it is still a REACT child
      // of that row and React bubbles synthetic events along the react tree —
      // so picking a vet or pressing Save ALSO fired the row's rowClick and
      // navigated the master to the pet page mid-assign (reported 2026-08-27).
      // The button's own stopPropagation never covered this: the leak comes
      // from the dialog's clicks, not the trigger's. MUI's own backdrop/close
      // handlers sit below this node, so closing still works.
      onClick={(e) => e.stopPropagation()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{translate('pos.clinic_team.assign_title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('pos.clinic_team.assign_description', { pet: petName || '' })}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {translate('pos.clinic_team.assigned_vets')}
        </Typography>
        <FormGroup data-testid="assign-vet-list">
          {identity?.id != null && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={ticked.has(Number(identity.id))}
                  onChange={() => toggle(Number(identity.id))}
                />
              }
              label={translate('pos.clinic_team.assign_to_me')}
            />
          )}
          {subs
            // A sub-account that never accepted its invitation (or was disabled
            // / removed) is not a delegate: they cannot sign in, and
            // getAlertRecipientsV2 bounces their patients' alerts back to the
            // master. Offering them was how a master could hand a patient to a
            // dead account and silently drop off its own alert list.
            //
            // NOT rendered-but-disabled: a disabled control can still be
            // driven programmatically, so "looks unpickable" is not "is
            // unpickable". The one exception is a CURRENT holder — it has to
            // stay listed or Save would silently drop it. A missing status is
            // pending_invitation (VP-TEAM-R04), never active — see
            // effectiveSubAccountStatus for why.
            .filter(
              (sub: any) =>
                effectiveSubAccountStatus(sub) === 'active' || ticked.has(Number(sub.id)),
            )
            .map((sub: any) => {
              const status = effectiveSubAccountStatus(sub)
              // Two DIFFERENT ways a delegate fails to receive alerts, and they
              // need different words. Non-active = cannot sign in, and the
              // backend bounces their alerts back to the master (so the patient
              // is still covered). Muted = an ACTIVE vet with
              // emailAlertsEnabled false, where getAlertRecipientsV2 filters
              // them out with NO fallback and nobody is emailed at all. Only the
              // second is a silent hole, so it is only worth annotating on a
              // sub that is otherwise a valid delegate.
              const suffix =
                status !== 'active'
                  ? ` — ${translate(`pos.clinic_team.status_${status}`)}`
                  : sub.emailAlertsEnabled === false
                    ? ` — ${translate('pos.clinic_team.assign_alerts_off')}`
                    : ''
              return (
                <FormControlLabel
                  key={sub.id}
                  control={
                    <Checkbox
                      checked={ticked.has(Number(sub.id))}
                      onChange={() => toggle(Number(sub.id))}
                    />
                  }
                  label={`${sub.name || sub.email}${suffix}`}
                />
              )
            })}
        </FormGroup>
        {ticked.size === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {translate('pos.clinic_team.unassigned')}
          </Typography>
        )}
        {mutedTicked.map((id) => (
          <Alert key={id} severity="warning" sx={{ mt: 2 }} data-testid="assign-vet-muted-warning">
            {translate('pos.clinic_team.assign_muted_warning_for', { vet: nameOf(id) })}
          </Alert>
        ))}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={submitting}>
          {translate('ra.action.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting || !teamLoaded}>
          {translate('pos.clinic_team.assign_submit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignVetModal
